import React, { useState } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { FormField, FormReadonlyField, FormTextareaField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { Alert } from 'antd'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import {
  TradingAccount,
  TransactionDto,
  WalletTransaction,
} from 'api/Transaction/Types/Transaction'
import { TransactionNoteDto } from 'api/Transaction/Types/TransactionNoteDto'
import { isIBCommission } from 'api/Wallet/Types/TransactionTypeDto'
import { UpdateWalletComplexTransfer } from 'api/Wallet/Types/UpdateWalletComplexTransfer'
import { Link } from 'components/Link'
import MoreButton from 'components/MoreButton/MoreButton'
import { MainTemplate } from 'components/Templates/MainTemplate'
import { TransactionMenu } from 'components/TransactionMenu/TransactionMenu'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { File } from 'lucide-react'
import { pageableSettings } from 'store/grid/grid.slice'
import { formatLocalDateUTC } from 'utils/date'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'
import stringComparator from 'utils/stringComparator'
import {
  isNotAllowedToChangeState,
  isTransactionPendingStatus,
} from 'utils/transaction/status.index'

import { TransactionDetailsTab } from '../DetailTabs'
import { filterTransactionNotes, NoteTextList } from '../NoteList'
import { transactionDetailsAdjustmentTradingAccountColumns } from '../TransactionDetailsAdjustmentWallet/transactionDetailsAdjustmentTradingAccountColumns'
import { transactionDetailsAdjustmentWalletColumns } from '../TransactionDetailsAdjustmentWallet/transactionDetailsAdjustmentWalletColumns'
import { ContentField } from '../TransactionDetailsFieldHelpers'

interface TransactionDetailsAdjustmentWalletAndPaymentAgentFormValues {
  id: string
  state: { id: number; name: string }
  client?: { id: string; name: string }
  referenceId: string
  type: {
    id: number
    name: string
  }
  createdDate: string
  comment: string | null
  walletTransactions: WalletTransaction[]
  tradingAccountTransactions: TradingAccount[]
}

const TransactionDetailsAdjustmentWalletAndPaymentAgentFormUI: React.FC<
  FormikProps<TransactionDetailsAdjustmentWalletAndPaymentAgentFormValues> & OuterProps
> = (props) => {
  const {
    values,
    filter,
    paginationState,
    permissions,
    transaction,
    notes,
    setAuditLogModal,
    showDocumentPreviewModal,
    handlePageChange,
    handleFilterChange,
    resetForm,
    setFieldValue,
    isEdit,
    setEdit,
  } = props

  const [isEditWalletTransaction, setEditWalletTransaction] = useState<WalletTransaction | null>(
    null
  )
  const [amount, setAmount] = useState<number | null>(null)

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    props.handleSubmit()
  }

  const openModal = (walletTransaction: WalletTransaction) => {
    setEditWalletTransaction(walletTransaction)
    setAmount(walletTransaction.amount)
  }

  const changeWalletTransaction = () => {
    const walletTransaction = values.walletTransactions.find(
      (x) => x.id === isEditWalletTransaction?.id
    )
    if (walletTransaction) {
      setFieldValue('walletTransactions', [
        ...values.walletTransactions
          .filter((x) => x.id !== walletTransaction.id)
          .sort((a, b) => stringComparator(a.id, b.id)),
        {
          ...walletTransaction,
          amount,
        },
      ])
    }
    setEditWalletTransaction(null)
    setAmount(null)
  }

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const findWalletTransactionsPaymentAgent = () => {
    return values.walletTransactions.find((t) => t.paymentAgentTransaction?.documentId)
      ?.paymentAgentTransaction
  }

  const getGridData = () => {
    const transactions =
      values.walletTransactions.length > 0
        ? filterBy(values.walletTransactions, filter)
        : filterBy(values.tradingAccountTransactions || [], filter)

    return [...transactions].sort(
      (a: WalletTransaction | TradingAccount, b: WalletTransaction | TradingAccount) =>
        stringComparator(a.id, b.id)
    )
  }

  const paymentAgentTransaction = findWalletTransactionsPaymentAgent()
  const gridData = getGridData()
  const transactionNotes = filterTransactionNotes(notes)
  const gridTotal =
    values.walletTransactions.length > 0
      ? values.walletTransactions.length
      : values.tradingAccountTransactions.length

  const getColumns = () => {
    if (values.tradingAccountTransactions.length > 0) {
      return transactionDetailsAdjustmentTradingAccountColumns
    } else {
      return transactionDetailsAdjustmentWalletColumns
    }
  }
  const columns = getColumns()

  return (
    <React.Fragment>
      {isEditWalletTransaction && amount !== null && (
        <Dialog
          open={!!isEditWalletTransaction}
          onOpenChange={(open) => !open && setEditWalletTransaction(null)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Transaction Line</DialogTitle>
            </DialogHeader>
            <div className='flex flex-col gap-4'>
              <FormReadonlyField label='Type' value={isEditWalletTransaction.directionType.name} />
              <FormReadonlyField label='Wallet' value={isEditWalletTransaction.wallet.name} />
              <FormReadonlyField label='Rate' value={isEditWalletTransaction.currencyRate} />
              <FormField
                label='Amount'
                name='amount'
                type='number'
                value={amount}
                onChange={(e) => {
                  setAmount(Number(e.target.value))
                }}
              />
              {!isIBCommission(props.values.type.id) && transactionNotes.length > 0 && (
                <ContentField label='Transaction Notes'>
                  <div className='w-full'>
                    <NoteTextList transactionNotes={transactionNotes} />
                  </div>
                </ContentField>
              )}
              <FormReadonlyField
                label='Currency'
                value={isEditWalletTransaction.wallet.currency.id}
              />
            </div>
            <DialogFooter>
              <Button type='button' onClick={() => changeWalletTransaction()}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <MainTemplate>
        <div className='px-4 md:px-6'>
          <Form onSubmit={handleSubmitForm}>
            <div className='flex flex-col gap-4'>
              {!isIBCommission(props.values.type.id) && transactionNotes.length > 0 && (
                <Alert
                  type='error'
                  message={
                    <>
                      There are notes related to the client(s) involved in this transaction. To view
                      please
                      <Button
                        type='button'
                        variant='link'
                        onClick={() => props.setActiveTab(TransactionDetailsTab.NOTES)}
                      >
                        Click Here
                      </Button>
                    </>
                  }
                />
              )}

              <DataGrid
                rightAddon={
                  <ButtonGroup>
                    {!!paymentAgentTransaction && (
                      <Button
                        type='button'
                        size='icon'
                        variant='outline'
                        onClick={() =>
                          showDocumentPreviewModal({
                            id: paymentAgentTransaction.documentId,
                            name: paymentAgentTransaction.documentId,
                          })
                        }
                      >
                        <File />
                      </Button>
                    )}
                    {isEdit ? (
                      <>
                        <Button
                          variant='outline'
                          type='button'
                          onClick={cancel}
                          data-test='cancel-button'
                        >
                          Cancel
                        </Button>
                        <Button disabled={props.isSubmitting} type='submit' data-test='save-button'>
                          Save
                          {props.isSubmitting && <Spinner data-icon='inline-start' />}
                        </Button>
                      </>
                    ) : (
                      <Button
                        type='button'
                        disabled={
                          !hasPermission(permissions, PermissionsEnum.CanEditTransactions) ||
                          isTransactionPendingStatus(values.state.id) ||
                          isNotAllowedToChangeState(values.state.id)
                        }
                        onClick={() => setEdit(true)}
                        data-test='edit-button'
                      >
                        Edit
                      </Button>
                    )}
                    <TransactionMenu
                      permissions={permissions}
                      handleAuditLog={() => setAuditLogModal(true)}
                    />
                  </ButtonGroup>
                }
              >
                <DataColumn>
                  <DataCard title='Transaction Overview'>
                    <FormReadonlyField label='Type' value={values.type.name} />
                    <FormReadonlyField
                      label='Date'
                      value={formatLocalDateUTC(values.createdDate, true)?.toString()}
                    />
                    <FormTextareaField
                      label='Client Comment'
                      name='comment'
                      readOnly={!isEdit}
                      maxLength={130}
                    />
                    {transaction.reverseTransaction && (
                      <FormReadonlyField
                        value={transaction.reverseTransaction.name}
                        label='Reverse Transaction'
                        link={generatePath('/transactions/:id', {
                          id: transaction.reverseTransaction.id,
                        })}
                      />
                    )}
                    {transaction.reversedTransactions?.length ? (
                      <ContentField label='Reversed Transactions'>
                        <div className='w-full rounded-md border border-input bg-muted/30 px-3 py-2 text-sm'>
                          <ul className='list-disc pl-4'>
                            {transaction.reversedTransactions.map((reversedTransaction) => (
                              <li key={reversedTransaction.id}>
                                <Link
                                  to={{
                                    pathname: generatePath('/transactions/:id', {
                                      id: reversedTransaction.id,
                                    }),
                                  }}
                                  className='font-medium text-primary hover:underline'
                                >
                                  {reversedTransaction.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </ContentField>
                    ) : null}
                  </DataCard>

                  <DataCard title='Details'>
                    <Grid
                      filterable
                      data={gridData.slice(
                        paginationState.skip,
                        paginationState.take + paginationState.skip
                      )}
                      filter={filter}
                      total={gridTotal}
                      take={paginationState.take}
                      skip={paginationState.skip}
                      pageable={pageableSettings}
                      onFilterChange={handleFilterChange}
                      onPageChange={handlePageChange}
                      data-test='table'
                      style={{ width: '100%' }}
                    >
                      {columns.map((column, idx) => (
                        <Column
                          key={idx}
                          field={column.field}
                          title={column.title}
                          className={column.className}
                          cell={column.cell}
                          width={column.width}
                          filterable={column.filterable}
                          filterCell={column.filterCell}
                        />
                      ))}
                      <Column
                        title=''
                        filterable={false}
                        cell={(gridCellProps: GridCellProps) => (
                          <td data-test='more-button-column'>
                            <MoreButton
                              disabled={isNotAllowedToChangeState(values.state.id)}
                              items={[
                                {
                                  name: 'Edit',
                                  disabled: !isEdit,
                                  onClick: () => openModal(gridCellProps.dataItem),
                                  'data-test': 'edit-button',
                                },
                                {
                                  name: 'Delete',
                                  disabled: !isEdit,
                                  onClick: () => {
                                    setFieldValue(
                                      'walletTransactions',
                                      values.walletTransactions.filter(
                                        (transactionItem) =>
                                          transactionItem.id !== gridCellProps.dataItem.id
                                      )
                                    )
                                  },
                                  'data-test': 'delete-button',
                                },
                              ]}
                              data-test='more-button'
                            />
                          </td>
                        )}
                      />
                    </Grid>
                  </DataCard>
                </DataColumn>
              </DataGrid>
            </div>
          </Form>
        </div>
      </MainTemplate>
    </React.Fragment>
  )
}

function dataToSubmit(
  values: TransactionDetailsAdjustmentWalletAndPaymentAgentFormValues
): FormSubmitValues<UpdateWalletComplexTransfer> {
  return {
    comment: values.comment,
    referenceId: values.referenceId,
    transactionId: values.id,
    transactions: values.walletTransactions.map((x) => ({
      amount: x.amount,
      directionTypeId: x.directionType.id,
      walletId: x.wallet.id,
    })),
  }
}

export interface TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralProps {
  transaction: TransactionDto
  permissions: PermissionDto[]
  client: { id: string; name: string } | undefined
  setActiveTab: (tabName: string) => void

  setAuditLogModal(value: boolean): void
}

interface OuterProps extends TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralProps {
  notes: TransactionNoteDto[]
  filter: CompositeFilterDescriptor
  paginationState: Page
  path: string
  isEdit: boolean

  setEdit(val: boolean): void
  showDocumentPreviewModal(document: { id: string; name: string }): Promise<void>
  onSubmit(values: FormSubmitValues<UpdateWalletComplexTransfer>): void
  handlePageChange(event: GridPageChangeEvent): void
  handleFilterChange(event: GridFilterChangeEvent): void
}

export const TransactionDetailsAdjustmentWalletAndPaymentAgentGeneralForm = withFormik<
  OuterProps,
  TransactionDetailsAdjustmentWalletAndPaymentAgentFormValues
>({
  mapPropsToValues: ({ transaction, client }) => {
    return {
      id: transaction.id,
      client,
      comment: transaction.comment,
      createdDate: transaction.createdDate,
      referenceId: transaction.referenceId,
      state: transaction.state,
      type: transaction.type,
      walletTransactions: transaction.walletTransactions,
      tradingAccountTransactions: transaction.tradingAccountTransactions,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
      props.setEdit(false)
    } catch (error) {
      console.error(error)
      props.setEdit(true)
    } finally {
      setSubmitting(false)
    }
  },
  validate: () => {
    const errors: FormikErrors<TransactionDetailsAdjustmentWalletAndPaymentAgentFormValues> = {}
    return errors
  },
  enableReinitialize: true,
})(TransactionDetailsAdjustmentWalletAndPaymentAgentFormUI)
